import type React from "react";
import { createContext, useContext, useState, useEffect } from "react";
import type { SvgIconComponent } from "@mui/icons-material";
interface PageTitleContextType {
  title: string;
  setTitle: (title: string) => void;
  contractNumber: string | null;
  setContractNumber: (contractNumber: string | null) => void;
  icon: SvgIconComponent | null;
  setIcon: (icon: SvgIconComponent | null) => void;
}
const PageTitleContext = createContext<PageTitleContextType | undefined>(
  undefined
);
export function PageTitleProvider({ children }: { children: React.ReactNode }) {
  const [title, setTitle] = useState("Tiba Vision");
  const [contractNumber, setContractNumber] = useState<string | null>(null);
  const [icon, setIcon] = useState<SvgIconComponent | null>(null);
  return (
    <PageTitleContext.Provider
      value={{
        title,
        setTitle,
        contractNumber,
        setContractNumber,
        icon,
        setIcon,
      }}
    >
      {children}
    </PageTitleContext.Provider>
  );
}
export function usePageTitle() {
  const context = useContext(PageTitleContext);
  if (context === undefined) {
    throw new Error("usePageTitle must be used within a PageTitleProvider");
  }
  return context;
}
// Hook for setting page title in components
export function useSetPageTitle(title: string) {
  const { setTitle } = usePageTitle();
  useEffect(() => {
    setTitle(title);
    return () => setTitle("Tiba Vision"); // Reset on unmount
  }, [title, setTitle]);
}
// Hook for contract number management
export function useContractNumber() {
  const { contractNumber, setContractNumber } = usePageTitle();
  return { contractNumber, setContractNumber };
}
// Hook for setting contract number in components
export function useSetContractNumber(contractNumber: string | null) {
  const { setContractNumber } = usePageTitle();
  useEffect(() => {
    setContractNumber(contractNumber);
    return () => setContractNumber(null); // Reset on unmount
  }, [contractNumber, setContractNumber]);
}
// Hook for icon management
export function usePageIcon() {
  const { icon, setIcon } = usePageTitle();
  return { icon, setIcon };
}
// Hook for setting page icon in components
export function useSetPageIcon(icon: SvgIconComponent | null) {
  const { setIcon } = usePageTitle();
  useEffect(() => {
    setIcon(icon);
    return () => setIcon(null); // Reset on unmount
  }, [icon, setIcon]);
}
